import {useEffect, useRef} from 'react'
import {useLocation} from 'react-router-dom'
import {config} from '../../../config'
import {isAppRoute} from '../../../shared/app-routes'

const REDACTED_PATHS = [
    [/^\/invite\/[^/]+/, '/invite/:code'],
    [/^\/u\/[^/]+/, '/u/:profile'],
    [/^\/network\/connections\/[^/]+/, '/network/connections/:user'],
    [/^\/network\/messages\/[^/]+/, '/network/messages/:user'],
    [/^\/network\/discussions\/[^/]+/, '/network/discussions/:thread'],
]
const STATIC_ROOT_PATHS = new Set(['auth', 'explore', 'following', 'invite', 'legal', 'network', 'profile', 'rankings'])
let trackerPromise

export const PLAUSIBLE_EVENTS = Object.freeze({
    landingSectionViewed: 'Landing Section Viewed',
    signup: 'Signup',
    mediaImportStarted: 'Media Import Started',
    mediaImportCompleted: 'Media Import Completed',
    followCreated: 'Follow Created',
})
export const usePlausibleSectionView = ({ref, section, enabled = true}) => {
    const trackedRef = useRef(false)
    useEffect(() => {
        if (!enabled || trackedRef.current || !ref.current || typeof IntersectionObserver === 'undefined') return
        const observer = new IntersectionObserver(([entry]) => {
            if (!entry.isIntersecting || entry.intersectionRatio < 0.35 || trackedRef.current) return
            trackedRef.current = true
            trackPlausibleEvent(PLAUSIBLE_EVENTS.landingSectionViewed, {props: {section}})
            observer.disconnect()
        }, {threshold: 0.35})
        observer.observe(ref.current)
        return () => observer.disconnect()
    }, [enabled, ref, section])
}
export const usePlausibleAnalytics = () => {
    const {pathname} = useLocation()
    useEffect(() => {
        if (!config.analytics.plausible.domain || !isAppRoute(pathname)) return
        initializePlausible()
            .then(({track}) => track('pageview', {url: new URL(pathname, window.location.origin).toString()}))
            .catch(() => {})
    }, [pathname])
}
export const trackPlausibleEvent = (eventName, options = {}) => {
    if (!config.analytics.plausible.domain || !isAppRoute()) return
    initializePlausible()
        .then(({track}) => track(eventName, options))
        .catch(() => {})
}
export const getPlausiblePath = pathname => {
    const redacted = REDACTED_PATHS.find(([pattern]) => pattern.test(pathname))
    if (redacted) return pathname.replace(...redacted)
    const [, rootPath] = pathname.split('/')
    if (rootPath && !STATIC_ROOT_PATHS.has(rootPath)) return pathname.replace(/^\/[^/]+/, '/:profile')
    return pathname
}
export const getPlausibleReferrer = (referrer, currentOrigin = window.location.origin) => {
    if (!referrer) return null
    try {
        const url = new URL(referrer)
        if (!['http:', 'https:'].includes(url.protocol) || url.origin === currentOrigin) return null
        return url.origin
    } catch {
        return null
    }
}
const initializePlausible = () => {
    if (!trackerPromise) trackerPromise = import('@plausible-analytics/tracker').then(tracker => {
        tracker.init({
            domain: config.analytics.plausible.domain,
            autoCapturePageviews: false,
            captureOnLocalhost: config.analytics.plausible.captureOnLocalhost,
            outboundLinks: false,
            fileDownloads: false,
            formSubmissions: false,
            logging: false,
            transformRequest: sanitizeRequest,
        })
        return tracker
    })
    return trackerPromise
}
const sanitizeRequest = payload => {
    if (payload.n === 'engagement') return null
    const url = new URL(payload.u, window.location.origin)
    if (!isAppRoute(url.pathname)) return null
    url.pathname = getPlausiblePath(url.pathname)
    url.search = ''
    url.hash = ''
    return {...payload, u: url.toString(), r: getPlausibleReferrer(payload.r)}
}
